type ShortcutEvent = {
  target: EventTarget | null;
  type?: string;
  defaultPrevented?: boolean;
  isComposing?: boolean;
  keyCode?: number;
  metaKey?: boolean;
  ctrlKey?: boolean;
  composedPath?: () => EventTarget[];
};
type ControlNode = {
  tagName: string;
  getAttribute: (name: string) => string | null;
  parentElement?: unknown;
  isContentEditable?: boolean;
};
function isControlNode(value: unknown): value is ControlNode {
  return Boolean(
    value &&
    typeof value === "object" &&
    "tagName" in value &&
    typeof value.tagName === "string" &&
    "getAttribute" in value &&
    typeof value.getAttribute === "function",
  );
}
const roles = new Set([
  "textbox",
  "searchbox",
  "spinbutton",
  "slider",
  "separator",
  "combobox",
  "listbox",
  "option",
  "menu",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "dialog",
  "alertdialog",
  "grid",
  "tree",
  "treeitem",
]);
/** Protect controls, portals and shadow-root editors without depending on one window's DOM constructors. */
export function isEditorShortcutBlocked(event: ShortcutEvent): boolean {
  if (event.defaultPrevented || event.isComposing || event.keyCode === 229)
    return true;
  const path = event.composedPath?.() ?? [];
  const targets = path.length ? path : [event.target];
  const seen = new Set<unknown>();
  for (const target of targets) {
    let node: unknown = target;
    while (isControlNode(node) && !seen.has(node)) {
      seen.add(node);
      const tag = node.tagName.toUpperCase(),
        role = node.getAttribute("role"),
        editable = node.getAttribute("contenteditable");
      if (
        node.getAttribute("data-editor-shortcuts") === "ignore" ||
        node.isContentEditable ||
        (editable !== null && editable !== "false") ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(tag) ||
        role?.split(/\s+/).some((r) => roles.has(r))
      )
        return true;
      // Modified commands can still address the selection after a toolbar click.
      // Plain Space/arrows/Delete belong to the focused control.
      if (
        event.type === "keydown" &&
        !event.metaKey &&
        !event.ctrlKey &&
        (["BUTTON", "A", "SUMMARY"].includes(tag) ||
          ["button", "checkbox", "radio", "switch", "tab"].includes(role ?? ""))
      )
        return true;
      node = node.parentElement;
    }
  }
  return false;
}
